import { EvaluatedImage, EvaluatedValue, Input, InputId, NodeId, OutputId } from 'web';
import { DEFAULT_NODE_WIDTH, RESIZABLE_NODE_TYPES } from '@/consts/node-ui.consts';
import { mergeRouterNodeDataForInsert } from '@/consts/router-node.defaults';
import { useWorkflowStore } from '@/state/workflow.state';
import { HandleType } from '@/enums/handle-type.enum';
import { NodeType } from '@/enums/node-type.enum';
import { MODEL_INPUT_LIMITS } from '@/components/Nodes/ModelComponents/consts/model-input-limits.const';
import { categorizeNodesGroupStatus, convertChildPositionToAbsolute } from '@/components/Nodes/utils/nodeUtils';
import { makeWorkspaceFontFaceKey, type FontStyleCombination } from '@/utils/customFonts.utils';
import { getHandleId } from '../../Nodes/Utils';
import { createNewNode } from '../useGraph';
import { FlowGraph } from '../FlowGraph';
import { UndoRedoEntry } from '../undoRedo';
import { resolveNodePreviewImagePosterUrl } from './nodePreviewImagePosterUrl';
import { ModelBaseView } from './ModelBaseView';
import { PromptView } from './PromptView';
import { CompositorView } from './CompositorView';
import { GroupsManagerView } from './NodeGrouping/GroupsManagerView';
import { convertParameterValueToLegacyValue } from './ParameterView/ParameterView';
import type { BuiltInFont, FontOptions, Font } from 'web';
import type { BaseNodeData, Edge, EdgeId, Node } from '@/types/node';
import type { AiModelData, ModelBaseNodeData } from '@/types/nodes/model';
import type { Connection } from '../FlowComponents/FlowTour/ConnectionContext';
import type { DuplicateDragSession, DuplicateNodeOptions } from '../flowTypes';
import type { CompositorNodeV3 } from '@/types/nodes/compositor';
import type { MissingFontFaceReplacement } from '@/types/missingWorkflowFonts.types';
/**
 * Resolves the model identifier from node data, accounting for both
 * the deprecated `model.name` field and the newer `kind` system.
 */
function getModelIdentifier(nodeData: ModelBaseNodeData): string | null {
  if (nodeData.model?.name) {
    return nodeData.model.name;
  }

  const aiData = nodeData as AiModelData;
  if (!aiData.kind) {
    return null;
  }

  if (aiData.kind.type === 'wildcard' && aiData.kind.model) {
    const modelInfo = aiData.kind.model;
    if ('name' in modelInfo && modelInfo.name) {
      return modelInfo.name;
    }
  }

  if (aiData.kind.type !== 'wildcard') {
    return aiData.kind.type;
  }

  return null;
}

export interface DeleteNodesResult {
  preventedGroups?: NodeId[];
}

export interface DeleteNodesOptions {
  /** When true, reconnects predecessors to successors across the removed region (see {@link FlowGraph.removeNodesPreservingBridgingConnections}). */
  preserveBridgingConnections?: boolean;
}

export class FlowView {
  private groupsManagerView: GroupsManagerView;

  constructor(private graph: FlowGraph) {
    this.groupsManagerView = new GroupsManagerView(graph);
  }

  /**
   * Valid (weight, italic) pairs for a base font from the loaded WASM graph
   * (`weightSupport` × `italicSupport`), matching compositor behavior.
   */
  getBaseFontStyleCombinations(font: BuiltInFont): FontStyleCombination[] {
    const fontOptions: FontOptions = {
      font: { type: 'built_in', data: font },
      italic: false,
      weight: 400,
      width: 100,
    };
    return (
      this.graph.wasm.call((wasm) => {
        const weights = Array.from(wasm.weightSupport(fontOptions));
        const italicBytes = wasm.italicSupport(fontOptions);
        const italics = Array.from(italicBytes).map((b) => b !== 0);
        const combos: FontStyleCombination[] = [];
        for (const weight of weights) {
          for (const isItalic of italics) {
            combos.push({ weight, isItalic });
          }
        }
        return combos.length > 0 ? combos : [{ weight: 400, isItalic: false }];
      }) ?? [{ weight: 400, isItalic: false }]
    );
  }

  redraw() {
    this.graph.wasm.call((wasm) => wasm.redraw());
  }

  /// NODES
  getNode(nodeId: NodeId): Node {
    return this.graph.store.getNode(nodeId);
  }

  getNodeNullable(nodeId: NodeId): Node | undefined {
    return this.graph.store.getNodeNullable(nodeId);
  }

  getNodes(): Node[] {
    return this.graph.store.getNodes();
  }

  /**
   * Replaces missing custom font faces via {@link CompositorView} / WASM (same path as layer property edits).
   */
  applyMissingWorkspaceFontReplacements(replacements: MissingFontFaceReplacement[]): void {
    const fontsToReplaceMap = new Map(replacements.map((r) => [r.sourceFaceKey, r]));
    for (const node of this.getNodes()) {
      if (node.type !== 'compv3') {
        continue;
      }
      const compositor = node.data as CompositorNodeV3;
      const compositorView = new CompositorView(this.graph, node.id);
      for (const [layerIdStr, layer] of Object.entries(compositor.data.layers)) {
        if (layer.kind.type !== 'text') {
          continue;
        }
        const font = layer.kind.fontOptions.font;

        const faceKey = makeWorkspaceFontFaceKey(
          font.type === 'custom' ? font.data.resourceKey : font.data,
          layer.kind.fontOptions.weight,
          layer.kind.fontOptions.italic,
        );
        const rep = fontsToReplaceMap.get(faceKey);
        if (!rep) {
          continue;
        }
        const newFont: Font = { type: 'built_in', data: rep.replacementFontFamilyKey };
        compositorView.setLayerTextFontFace(Number(layerIdStr), newFont, rep.replacementWeight, rep.replacementItalic);
      }
    }
  }

  /**
   * Finds a node at the given flow position.
   * Returns the first matching node.
   *
   * @param flowPosition - The position in flow coordinates
   * @returns The node at the position, or undefined if no node is found
   */
  findNodeAtPosition(flowPosition: { x: number; y: number }): Node | undefined {
    const nodes = this.getNodes();
    return nodes.find((node) => {
      const rect = {
        left: node.position.x,
        top: node.position.y,
        right: node.position.x + (node.width ?? 0),
        bottom: node.position.y + (node.height ?? 0),
      };
      return (
        flowPosition.x >= rect.left &&
        flowPosition.x <= rect.right &&
        flowPosition.y >= rect.top &&
        flowPosition.y <= rect.bottom
      );
    });
  }

  addNewNode(newNode: Node, connection?: Connection) {
    this.graph.edit(() => {
      const undoEntry = this.graph.addNodes([newNode]);

      const intersectingGroupId = this.groupsManagerView.findIntersectingGroup([newNode]);
      if (intersectingGroupId) {
        undoEntry.add(this.groupsManagerView.attachNodesToGroup([newNode], intersectingGroupId));
      }

      if (connection?.handleType && connection.handleSide && connection.nodeId && connection.handleId) {
        // Find a compatible handle on the new node using WASM (source of truth for input/output types)
        const side = connection.handleSide === 'source' ? 'input' : 'output';
        let handleKey: string | undefined;

        if (side === 'input') {
          for (const inputId of this.graph.getInputIds(newNode.id)) {
            if (this.graph.nodeValidInputTypes(newNode.id, inputId).includes(connection.handleType)) {
              handleKey = inputId;
              break;
            }
          }
        } else {
          for (const outputId of this.graph.getOutputIds(newNode.id)) {
            if (this.graph.nodeOutputType(newNode.id, outputId) === connection.handleType) {
              handleKey = outputId;
              break;
            }
          }
        }

        if (!handleKey) {
          return undoEntry;
        }

        const newHandleId = getHandleId(newNode.id, connection.handleSide === 'source' ? 'input' : 'output', handleKey);
        if (!newHandleId) {
          return undoEntry;
        }

        if (connection.handleSide === 'source') {
          undoEntry.add(this.connectEdgeToTarget(connection.nodeId, newNode.id, connection.handleId, newHandleId));
        } else if (connection.handleSide === 'target') {
          undoEntry.add(this.connectEdgeToTarget(newNode.id, connection.nodeId, newHandleId, connection.handleId));
        }
      }
      return undoEntry;
    });
    this.graph.updateNodeOutputs(newNode.id, false);
  }

  /**
   * Adds multiple nodes and wraps them in a named group as a single undo operation.
   * Skips auto-attachment to existing groups so positions stay consistent.
   */
  addNodesAsGroup(newNodes: Node[], groupName: string) {
    const nodeIds = newNodes.map((n) => n.id);
    this.graph.setUndoBarrier();
    this.graph.edit(() => {
      const undoEntry = this.graph.addNodes(newNodes);
      undoEntry.add(this.groupsManagerView.createGroupFromNodes(nodeIds, groupName));
      return undoEntry;
    });
    for (const id of nodeIds) {
      this.graph.updateNodeOutputs(id, false);
    }
    this.graph.disableUndoBarrier();
  }

  /**
   * @deprecated Update node data through "View"s and FlowGraph instead.
   */
  updateNodeData<T extends Record<string, any>>(nodeId: NodeId, newData: Partial<T>) {
    this.graph.updateNodeData(nodeId, newData);
  }

  /**
   * Renames the node's display title via {@link Node.data.name}. When {@link Node.originalName} is still missing,
   * it is set from the previous title once (unsaved / legacy payloads); loaded recipes receive it from the API.
   */
  renameNodeDisplayName(nodeId: NodeId, newName: string) {
    this.graph.renameNodeDisplayName(nodeId, newName);
  }

  // TODO: Remove
  async loadResources() {
    await this.graph.loadResourcesAsync();
  }

  // TODO: Remove
  updateInputBeforeRun(nodeId: NodeId, nodeData: ModelBaseNodeData) {
    const parameterIds = this.graph.wasm.call((wasm) => wasm.parameterIds(nodeId)) ?? [];
    const params = Object.fromEntries(
      parameterIds.map((parameterId) => {
        const parameterValue = this.graph.wasm.call((wasm) => wasm.evaluateParameter(nodeId, parameterId));
        return [parameterId, parameterValue ? convertParameterValueToLegacyValue(parameterValue) : undefined];
      }),
    );
    const inputIds = this.graph.wasm.call((wasm) => wasm.inputIds(nodeId)) ?? [];
    const input = Object.fromEntries(
      inputIds.map((inputId) => {
        if (parameterIds.includes(inputId)) {
          return [inputId, params[inputId]];
        }

        const evaluatedInputValue = () => {
          const evaluateImageValue = (targetNodeId: NodeId, targetInputId: InputId) => {
            const input = this.graph.wasm.call((wasm) => wasm.nodeInput(targetNodeId, targetInputId));
            const img = input
              ? this.graph.wasm.call((wasm) => wasm.exportNodeRender(input.nodeId, input.outputId))
              : undefined;
            return img ? { type: 'image' as const, url: img.data, width: img.width, height: img.height } : undefined;
          };

          const inputValue = this.graph.evaluateInput(nodeId, inputId);

          if (!inputValue) return undefined;

          // If the input is a computed image, we need to render it
          if (inputValue.type === 'image' && inputValue.value.type === 'computed') {
            return evaluateImageValue(nodeId, inputId);
          }

          // When there is no video asset in the handle value, we return a placeholder object. This invokes
          // a server side computation of the video.
          // This is a temoprary fix until we refactor server side execution logic to support computed
          // assets more natively.
          if (inputValue.type === 'video' && inputValue.value.type === 'computed') {
            return {
              type: 'video_placeholder',
            };
          }

          if (inputValue.type === 'lora') {
            return `weavy-lora::${inputValue.value}`;
          }

          if (inputValue.type === 'kling_element') {
            const klingElementInput = this.graph.wasm.call((wasm) => wasm.nodeInput(nodeId, inputId));
            if (!klingElementInput) return undefined;

            const klingElementValue = inputValue.value;

            const resolveImage = (image: EvaluatedImage | undefined, inputId: string) => {
              if (image?.type === 'computed') {
                return evaluateImageValue(klingElementInput.nodeId, inputId);
              }
              return image;
            };

            return {
              type: 'kling_element',
              frontalImageUrl: resolveImage(klingElementValue.frontalImageUrl, 'frontal_image_url'),
              referenceImageUrl1: resolveImage(klingElementValue.referenceImageUrl1, 'reference_image_url1'),
              referenceImageUrl2: resolveImage(klingElementValue.referenceImageUrl2, 'reference_image_url2'),
              referenceImageUrl3: resolveImage(klingElementValue.referenceImageUrl3, 'reference_image_url3'),
            };
          }

          if (typeof inputValue.value === 'object' && !Array.isArray(inputValue.value)) {
            return { ...inputValue.value, type: inputValue.type };
          }
          return inputValue.value;
        };
        return [inputId, evaluatedInputValue()];
      }),
    );
    nodeData.params = { ...nodeData.params, ...input, ...params };
    nodeData.input = { ...nodeData.input, ...input };
  }

  pasteAndPositionNodesAndEdges(
    clipboardNodes: Node[],
    clipboardEdges: Edge[],
    positionDuplicatedNodes: (nodes: Node[], newNodeIds: NodeId[]) => UndoRedoEntry,
    scrollNodeIntoViewIfNeeded: (nodes: Node[]) => void,
    options?: DuplicateNodeOptions,
  ) {
    this.graph.edit(() => {
      const { undoEntry, newNodeIds } = this.graph.duplicateNodesAndEdges(clipboardNodes, clipboardEdges, options);

      // Position the duplicated nodes
      undoEntry.add(positionDuplicatedNodes(clipboardNodes, newNodeIds));

      // Select the duplicated nodes and scroll them into view
      const newNodes = newNodeIds.map((id) => this.graph.store.getNode(id));
      this.selectedNodes = newNodeIds;
      scrollNodeIntoViewIfNeeded(newNodes);

      return undoEntry;
    });
  }

  duplicateNodes(
    nodeIds: NodeId[],
    positionDuplicatedNodes: (nodes: Node[], newNodeIds: NodeId[]) => UndoRedoEntry,
    scrollNodeIntoViewIfNeeded: (nodes: Node[]) => void,
    options?: DuplicateNodeOptions,
  ) {
    if (nodeIds.length === 0) return;
    const nodeIdsSet = new Set(nodeIds);

    const nodesIncludingChildren = nodeIds.flatMap((nodeId) => {
      const node = this.graph.store.getNode(nodeId);
      if (node.type === NodeType.CustomGroup) {
        return [node, ...this.getChildNodesOfGroup(node.id).filter((childNode) => !nodeIdsSet.has(childNode.id))];
      }
      return [node];
    });
    const nodesIncludingChildrenIdSet = new Set(nodesIncludingChildren.map((node) => node.id));

    const preserveExternalInputs = options?.preserveExternalInputs ?? false;
    const edgesToCopy = this.graph.store
      .getEdges()
      .filter((e) =>
        preserveExternalInputs
          ? nodesIncludingChildrenIdSet.has(e.target)
          : nodesIncludingChildrenIdSet.has(e.source) && nodesIncludingChildrenIdSet.has(e.target),
      );

    this.pasteAndPositionNodesAndEdges(
      nodesIncludingChildren,
      edgesToCopy,
      positionDuplicatedNodes,
      scrollNodeIntoViewIfNeeded,
      preserveExternalInputs ? { preserveExternalInputs: true } : undefined,
    );
  }

  /**
   * Duplicate nodes in place (overlapping originals) for Option/Alt+drag. Selects the new nodes.
   * Callers typically pass `{ preserveExternalInputs: true }` so upstream edges into the selection are re-wired to the copies (same semantics as {@link duplicateNodes}).
   * Returns session data used by {@link handleNodeDrag} to move duplicates while keeping originals fixed.
   */
  beginDuplicateDrag(nodeIds: NodeId[], options?: DuplicateNodeOptions): DuplicateDragSession | null {
    if (nodeIds.length === 0) return null;

    const nodeIdsSet = new Set(nodeIds);
    const nodesIncludingChildren = nodeIds.flatMap((nodeId) => {
      const node = this.graph.store.getNode(nodeId);
      if (node.type === NodeType.CustomGroup) {
        return [node, ...this.getChildNodesOfGroup(node.id).filter((childNode) => !nodeIdsSet.has(childNode.id))];
      }
      return [node];
    });

    const frozenPositions = new Map<NodeId, { x: number; y: number }>();
    for (const n of nodesIncludingChildren) {
      if (this.graph.getEffectiveIsLocked(n.id)) {
        return null;
      }
      frozenPositions.set(n.id, { ...n.position });
    }

    const nodesIncludingChildrenIdSet = new Set(nodesIncludingChildren.map((node) => node.id));
    const preserveExternalInputs = options?.preserveExternalInputs ?? false;
    const edgesToCopy = this.graph.store
      .getEdges()
      .filter((e) =>
        preserveExternalInputs
          ? nodesIncludingChildrenIdSet.has(e.target)
          : nodesIncludingChildrenIdSet.has(e.source) && nodesIncludingChildrenIdSet.has(e.target),
      );

    let session: DuplicateDragSession | null = null;

    this.graph.edit(() => {
      const { undoEntry, newNodeIds } = this.graph.duplicateNodesAndEdges(
        nodesIncludingChildren,
        edgesToCopy,
        preserveExternalInputs ? { preserveExternalInputs: true } : undefined,
      );
      const originalToNewId = new Map<NodeId, NodeId>();
      for (let i = 0; i < nodesIncludingChildren.length; i++) {
        const orig = nodesIncludingChildren[i];
        const newId = newNodeIds[i];
        if (orig && newId) {
          originalToNewId.set(orig.id, newId);
        }
      }
      this.selectedNodes = newNodeIds;
      session = { originalToNewId, frozenPositions };
      return undoEntry;
    }, false);

    return session;
  }

  setNodeDimensions(
    dimensions: { nodeId: NodeId; width?: number; height?: number; updateStyle?: boolean; updateData?: boolean }[],
  ): UndoRedoEntry {
    const undoEntry = UndoRedoEntry.empty();
    for (const dim of dimensions) {
      const { nodeId, width, height, updateData } = dim;
      const widthUpdate = 'width' in dim ? { width } : {};
      const heightUpdate = 'height' in dim ? { height } : {};
      const update = { ...widthUpdate, ...heightUpdate };
      if (Object.keys(update).length > 0) {
        undoEntry.add(
          this.graph.store.updateNode(nodeId, () => ({
            ...update,
          })),
        );
        if (updateData) {
          undoEntry.add(
            this.graph.store.updateNodeData<BaseNodeData & { height?: number; width?: number }>(nodeId, () => ({
              ...update,
            })),
          );
        }
      }

      if (this.graph.isNodeInGroup(nodeId)) {
        undoEntry.add(this.groupsManagerView.expandGroupAndRebaseChildrenIfNeeded(this.getNode(nodeId)));
      }
    }
    return undoEntry;
  }

  resizeNode(nodeId: NodeId, dimensions: { width?: number; height?: number }, ongoing: boolean): void {
    const nodeType = this.graph.store.getNodeNullable(nodeId)?.type;
    if (!nodeType || !RESIZABLE_NODE_TYPES.has(nodeType as NodeType)) return;
    this.graph.edit(() => this.setNodeDimensions([{ nodeId, ...dimensions, updateData: true }]), ongoing);
  }

  setNodePositions(positions: { nodeId: NodeId; position: { x: number; y: number } }[]): UndoRedoEntry {
    return this.graph.store.setNodePositions(positions);
  }

  editNodePositions(positions: { nodeId: NodeId; position: { x: number; y: number } }[], ongoing: boolean) {
    this.graph.cancelOngoingAction();
    if (
      !positions.every(({ nodeId, position }) => {
        const current = this.graph.store.getNode(nodeId).position;
        return (current.x - position.x) ** 2 + (current.y - position.y) ** 2 < 4;
      })
    ) {
      this.graph.edit(() => this.setNodePositions(positions), ongoing);
    }
  }

  setNodePositionsAndDimensions(
    positions: {
      nodeId: NodeId;
      position: { x: number; y: number };
      width?: number;
      height?: number;
    }[],
  ) {
    const undoEntry = UndoRedoEntry.empty();
    for (const { nodeId, position, width, height } of positions) {
      undoEntry.add(this.setNodePositions([{ nodeId, position }]));
      if (width !== undefined && height !== undefined) {
        undoEntry.add(this.setNodeDimensions([{ nodeId, width, height }]));
      }
    }
    return undoEntry;
  }

  moveNodesBy(nodes: NodeId[], delta: { x: number; y: number }) {
    this.graph.edit(
      () =>
        this.setNodePositions(
          nodes.map((nodeId) => {
            const current = this.graph.store.getNode(nodeId).position;
            const newPosition = { x: current.x + delta.x, y: current.y + delta.y };
            return { nodeId, position: newPosition };
          }),
        ),
      false,
    );
  }

  getEffectiveIsLocked(nodeId: NodeId): boolean {
    return this.graph.getEffectiveIsLocked(nodeId);
  }

  toggleLockNode(nodeId: NodeId) {
    this.graph.edit(() => {
      return this.graph.toggleNodeLocked(nodeId);
    });
  }

  /**
   * Filter out locked nodes and groups with locked children, update selection, track group deletes,
   * then remove the eligible nodes (optionally preserving edges across the gap).
   */
  deleteNodes(nodeIds: NodeId[], options?: DeleteNodesOptions): DeleteNodesResult {
    const result: DeleteNodesResult = {};
    const preserveBridging = options?.preserveBridgingConnections === true;

    this.graph.edit(() => {
      const { nodesToDelete, groupsWithLockedChildren } = nodeIds.reduce<{
        nodesToDelete: Node[];
        groupsWithLockedChildren: NodeId[];
      }>(
        (acc, nodeId) => {
          const node = this.graph.store.getNode(nodeId);
          if (this.graph.getEffectiveIsLocked(nodeId)) {
            return acc;
          }

          if (node.type !== NodeType.CustomGroup) {
            acc.nodesToDelete.push(node);
            return acc;
          }

          const groupChildren = this.graph.getChildNodesOfGroup(nodeId);
          if (groupChildren.some((child) => child.data.isLocked)) {
            acc.groupsWithLockedChildren.push(nodeId);
            return acc;
          } else {
            acc.nodesToDelete.push(node, ...groupChildren.filter((child) => !nodeIds.includes(child.id)));
            return acc;
          }
        },
        { nodesToDelete: [], groupsWithLockedChildren: [] },
      );

      if (groupsWithLockedChildren.length > 0) {
        result.preventedGroups = groupsWithLockedChildren;
      }

      const unlockedNodeIds = nodesToDelete.map((node) => node.id);
      this.selectedNodes = this.selectedNodes.filter((id) => !unlockedNodeIds.includes(id));
      const undoEntry = preserveBridging
        ? this.graph.removeNodesPreservingBridgingConnections(unlockedNodeIds)
        : this.graph.removeNodes(unlockedNodeIds);

      this.groupsManagerView.trackDeletedGroupNodes(nodesToDelete);

      return undoEntry;
    });

    return result;
  }

  deleteSelected(): DeleteNodesResult {
    if (this.selectedNodes.length > 0) {
      // deleting nodes deletes the relevant edges
      return this.deleteNodes(this.selectedNodes);
    } else if (this.selectedEdges.length > 0) {
      this.deleteEdges(this.selectedEdges);
      return {};
    }
    return {};
  }

  /**
   * See {@link FlowGraph.evaluateNode} for more information.
   */
  evaluateNode(nodeId: NodeId, index?: number): EvaluatedValue | undefined {
    return this.graph.evaluateNode(nodeId, index);
  }

  /**
   * See {@link FlowGraph.evaluateInput} for more information.
   */
  evaluateInput(nodeId: NodeId, inputId: InputId): EvaluatedValue | undefined {
    return this.graph.evaluateInput(nodeId, inputId);
  }

  /**
   * Poster URL for a node's image output ({@link resolveNodePreviewImagePosterUrl}). Prefer calling
   * from user actions; computed images trigger an expensive WASM export.
   */
  resolveNodePreviewImagePosterUrl(nodeId: NodeId): string | undefined {
    return resolveNodePreviewImagePosterUrl(this.graph, nodeId);
  }

  /**
   * See {@link FlowGraph.evaluateOutput} for more information.
   */
  evaluateOutput(input: Input): EvaluatedValue | undefined {
    return this.graph.evaluateOutput(input);
  }

  listSelectorOptions(nodeId: NodeId): string[] {
    const value = this.evaluateNode(nodeId);
    return value?.type === 'string_array' ? value.value : [];
  }

  /**
   * See {@link FlowGraph.isInputValid} for more information.
   */
  isInputValid(nodeId: NodeId, inputId: InputId): boolean {
    return this.graph.isInputValid(nodeId, inputId);
  }

  /**
   * See {@link FlowGraph.nodeValidInputTypes} for more information.
   */
  nodeValidInputTypes(nodeId: NodeId, inputId: InputId): Array<HandleType> {
    return this.graph.nodeValidInputTypes(nodeId, inputId);
  }

  /**
   * See {@link FlowGraph.nodeInputType} for more information.
   */
  nodeInputType(nodeId: NodeId, inputId: InputId): HandleType | undefined {
    return this.graph.nodeInputType(nodeId, inputId);
  }

  /**
   * See {@link FlowGraph.nodeOutputType} for more information.
   */
  nodeOutputType(nodeId: NodeId, outputId: OutputId): HandleType | undefined {
    return this.graph.nodeOutputType(nodeId, outputId);
  }

  isGroupNode(nodeId: NodeId): boolean {
    return this.graph.store.getNode(nodeId).type === NodeType.CustomGroup;
  }

  getChildNodesOfGroup(groupId: NodeId): Node[] {
    return this.graph.getChildNodesOfGroup(groupId);
  }

  /// EDGES

  getEdge(edgeId: EdgeId): Edge {
    return this.graph.store.getEdge(edgeId);
  }

  getEdgeNullable(edgeId: EdgeId): Edge | undefined {
    return this.graph.store.getEdgeNullable(edgeId);
  }

  wouldConnectionResultInCycle(source: NodeId, target: NodeId): boolean {
    return this.graph.wouldConnectionResultInCycle(source, target);
  }

  connectEdge(source: NodeId, target: NodeId, sourceHandle: string, targetHandle: string) {
    this.graph.edit(() => {
      return this.connectEdgeToTarget(source, target, sourceHandle, targetHandle);
    });
  }

  reconnectEdge(source: NodeId, target: NodeId, sourceHandle: string, targetHandle: string, oldEdge: Edge) {
    if (
      source === oldEdge.source &&
      sourceHandle === oldEdge.sourceHandle &&
      target === oldEdge.target &&
      targetHandle === oldEdge.targetHandle
    ) {
      // reconnecting edge to same handle, do nothing
      return;
    }
    this.graph.edit(() => {
      const undoEntry = this.graph.removeEdges([oldEdge.id]);
      undoEntry.add(this.connectEdgeToTarget(source, target, sourceHandle, targetHandle));
      return undoEntry;
    });
  }

  /**
   * Handles continuous drag operations and drag stop operations.
   * Each node is processed individually based on its own characteristics.
   * Updates positions. For drag stop, it also performs group attachment and removal from groups.
   */
  handleNodeDrag(
    nodes: Node[],
    isGroupModifierPressed: boolean,
    isOngoing: boolean = true,
    duplicateDragSession?: DuplicateDragSession | null,
  ): void {
    const effectiveGroupModifier = duplicateDragSession ? false : isGroupModifierPressed;

    const nodesForLogic =
      duplicateDragSession !== undefined && duplicateDragSession !== null
        ? nodes.flatMap((n) => {
            const newId = duplicateDragSession.originalToNewId.get(n.id);
            if (!newId) return [];
            const base = this.graph.store.getNode(newId);
            return [{ ...base, position: n.position }];
          })
        : nodes;

    this.graph.edit(() => {
      const undoEntry = UndoRedoEntry.empty();

      if (duplicateDragSession) {
        for (const n of nodes) {
          const frozen = duplicateDragSession.frozenPositions.get(n.id);
          if (frozen) {
            undoEntry.add(this.setNodePositions([{ nodeId: n.id, position: frozen }]));
          }
        }
      }

      const { groupNodes, groupedNodes, ungroupedNodes } = categorizeNodesGroupStatus(nodesForLogic);
      const draggedGroupIds = new Set(groupNodes.map((node) => node.id));

      // Update positions for group nodes and ungrouped nodes (simple position updates)
      for (const node of [...groupNodes, ...ungroupedNodes]) {
        undoEntry.add(this.setNodePositions([{ nodeId: node.id, position: node.position }]));
      }

      // Handle grouped nodes specially (may expand group or update position)
      undoEntry.add(this.handleGroupedNodesDrag(groupedNodes, draggedGroupIds, effectiveGroupModifier));

      // Handle drag stop (attachment/detachment)
      if (!isOngoing) {
        undoEntry.add(
          this.handleDragStop(groupedNodes, groupNodes, ungroupedNodes, draggedGroupIds, effectiveGroupModifier),
        );
      }

      return undoEntry;
    }, isOngoing);
  }

  /**
   * Handles drag operations for nodes that are inside groups.
   * - If modifier is pressed: updates position (preparing to detach on drag stop)
   * - If dragged without its group: expand/rebase group bounds so the frame and relative coords stay correct
   * - Otherwise: position is handled by parent group drag
   */
  private handleGroupedNodesDrag(
    groupedNodes: Node[],
    draggedGroupIds: Set<string>,
    isGroupModifierPressed: boolean,
  ): UndoRedoEntry {
    const undoEntry = UndoRedoEntry.empty();

    for (const node of groupedNodes) {
      const isDraggedWithoutItsGroup = !draggedGroupIds.has(node.parentId!);

      if (isGroupModifierPressed) {
        // Modifier pressed: update position (will be detached on drag stop)
        undoEntry.add(this.setNodePositions([{ nodeId: node.id, position: node.position }]));
      } else if (isDraggedWithoutItsGroup) {
        // Dragged without parent group: expand the group to accommodate
        undoEntry.add(this.groupsManagerView.expandGroupAndRebaseChildrenIfNeeded(node, true));
      }
      // Else: node is dragged with its parent group, position handled by parent
    }

    return undoEntry;
  }

  /**
   * Handles node attachment/detachment when drag operation completes.
   * - If modifier is pressed: detach grouped nodes from their groups
   * - Otherwise: attach nodes to groups they intersect with
   */
  private handleDragStop(
    groupedNodes: Node[],
    groupNodes: Node[],
    ungroupedNodes: Node[],
    draggedGroupIds: Set<string>,
    isGroupModifierPressed: boolean,
  ): UndoRedoEntry {
    const undoEntry = UndoRedoEntry.empty();

    if (isGroupModifierPressed) {
      undoEntry.add(this.detachNodesFromGroups(groupedNodes, draggedGroupIds));
    } else {
      const groupAttachments = this.findUngroupedNodesIntersectingWithGroups(groupNodes);
      const nodeGroupId = this.findGroupIntersectingWithNodes(ungroupedNodes);

      for (const { groupId, nodes: nodesToAttach } of groupAttachments) {
        undoEntry.add(this.groupsManagerView.attachNodesToGroup(nodesToAttach, groupId));
      }

      if (nodeGroupId) {
        undoEntry.add(this.groupsManagerView.attachNodesToGroup(ungroupedNodes, nodeGroupId));
      }
    }

    return undoEntry;
  }

  private detachNodesFromGroups(groupedNodes: Node[], draggedGroupIds: Set<string>): UndoRedoEntry {
    const nodesToRemove = groupedNodes.filter((node) => !node.parentId || !draggedGroupIds.has(node.parentId));
    return nodesToRemove.length > 0
      ? this.groupsManagerView.removeNodesFromGroup(nodesToRemove)
      : UndoRedoEntry.empty();
  }

  isNodeInGroup(node: Node): boolean {
    return !!node.parentId;
  }

  removeNodesFromGroup(NodeIds: NodeId[]) {
    this.graph.edit(() => {
      const nodes = NodeIds.map((nodeId) => this.graph.store.getNode(nodeId));
      return this.groupsManagerView.removeNodesFromGroup(nodes);
    });
  }

  getParentGroup(nodeId: NodeId): string | undefined {
    return this.graph.store.getNodeNullable(nodeId)?.parentId;
  }

  /**
   * Pure query method: Gets the group ID that intersects with ungrouped nodes
   * Used for highlighting during UNGROUPED drag state
   */
  getIntersectingGroupForNodes(nodes: Node[]): string | null {
    return this.groupsManagerView.findIntersectingGroup(nodes);
  }

  /**
   * Finds which stationary nodes each group currently intersects with.
   * Returns an array where each entry represents a group and the nodes that partially intersects with it.
   *
   * @param groupNodes - Array of group nodes
   */
  findUngroupedNodesIntersectingWithGroups(groupNodes: Node[]): Array<{ groupId: string; nodes: Node[] }> {
    return this.groupsManagerView.findUngroupedNodesIntersectingWithGroups(groupNodes);
  }

  /**
   * Finds which group (if any) the given nodes intersects with.
   * Returns the group ID or null if no suitable group is found.
   *
   * @param nodes - Array of nodes
   */
  findGroupIntersectingWithNodes(nodes: Node[]): string | null {
    return this.groupsManagerView.findIntersectingGroup(nodes);
  }

  attachNodesToGroup(nodes: Node[], groupId: string): UndoRedoEntry {
    return this.groupsManagerView.attachNodesToGroup(nodes, groupId);
  }

  /**
   * Inserts a Router node on an output handle. The router's input is connected
   * to the source output, and all edges that were connected to that output
   * are rewired to come from the router's output instead.
   */
  insertRouterOnOutput(sourceNodeId: NodeId, sourceHandleId: string) {
    const sourceNode = this.graph.store.getNodeNullable(sourceNodeId);
    if (!sourceNode) return;

    const nodeTypes = useWorkflowStore.getState().nodeTypes;
    const routerDefinition = nodeTypes.find((n) => n.type === 'router');
    if (!routerDefinition) return;

    const ROUTER_OFFSET_X = 120;
    const ROUTER_OFFSET_Y = 60;

    // Child nodes in a group store positions relative to the group. attachNodesToGroup and
    // calculateExpandedGroupBounds expect absolute flow coordinates before conversion.
    let position: { x: number; y: number };
    if (sourceNode.parentId) {
      const parentGroup = this.graph.store.getNodeNullable(sourceNode.parentId);
      if (parentGroup) {
        const sourceAbsolute = convertChildPositionToAbsolute(sourceNode, parentGroup.position).position;
        position = {
          x: sourceAbsolute.x + (sourceNode.width ?? DEFAULT_NODE_WIDTH) + ROUTER_OFFSET_X,
          y: sourceAbsolute.y + ROUTER_OFFSET_Y,
        };
      } else {
        position = {
          x: sourceNode.position.x + (sourceNode.width ?? DEFAULT_NODE_WIDTH) + ROUTER_OFFSET_X,
          y: sourceNode.position.y + ROUTER_OFFSET_Y,
        };
      }
    } else {
      position = {
        x: sourceNode.position.x + (sourceNode.width ?? DEFAULT_NODE_WIDTH) + ROUTER_OFFSET_X,
        y: sourceNode.position.y + ROUTER_OFFSET_Y,
      };
    }

    const routerNode = createNewNode(routerDefinition.id, nodeTypes, position);
    if (!routerNode) return;

    routerNode.data = mergeRouterNodeDataForInsert(routerNode.data);

    const routerNodeId = routerNode.id;
    const outgoingEdges = this.graph.store.getEdges().filter((edge) => edge.sourceHandle === sourceHandleId);

    this.graph.edit(() => {
      const undoEntry = this.graph.addNodes([routerNode]);

      // Same group as source: use attachNodesToGroup so parentId, WASM parent, relative position, and group bounds stay in sync.
      if (sourceNode.parentId) {
        undoEntry.add(this.groupsManagerView.attachNodesToGroup([routerNode], sourceNode.parentId));
      } else {
        const intersectingGroupId = this.groupsManagerView.findIntersectingGroup([routerNode]);
        if (intersectingGroupId) {
          undoEntry.add(this.groupsManagerView.attachNodesToGroup([routerNode], intersectingGroupId));
        }
      }

      const routerInputKey = this.graph.getInputIds(routerNodeId)[0];
      const routerOutputKey = this.graph.getOutputIds(routerNodeId)[0];
      if (routerInputKey === undefined || routerOutputKey === undefined) {
        return undoEntry;
      }

      const routerInputHandle = getHandleId(routerNodeId, 'input', routerInputKey);
      undoEntry.add(this.connectEdgeToTarget(sourceNodeId, routerNodeId, sourceHandleId, routerInputHandle));

      const routerOutputHandle = getHandleId(routerNodeId, 'output', routerOutputKey);
      for (const edge of outgoingEdges) {
        undoEntry.add(this.graph.removeEdges([edge.id]));
        const newEdge = this.graph.createEdge(routerNodeId, edge.target, routerOutputHandle, edge.targetHandle!);
        undoEntry.add(this.graph.addEdges([newEdge]));
      }

      return undoEntry;
    });

    this.graph.updateNodeOutputs(routerNodeId, false);
    this.selectedNodes = [routerNodeId];
  }

  private connectEdgeToTarget(
    source: NodeId,
    target: NodeId,
    sourceHandle: string,
    targetHandle: string,
  ): UndoRedoEntry {
    const newEdge = this.graph.createEdge(source, target, sourceHandle, targetHandle);
    const existingEdge = this.graph.store
      .getEdges()
      .find((edge) => edge.target === newEdge.target && edge.targetHandle === newEdge.targetHandle);
    if (existingEdge) {
      const undoEntry = UndoRedoEntry.empty();
      undoEntry.add(this.graph.removeEdges([existingEdge.id]));
      undoEntry.add(this.graph.addEdges([newEdge]));
      return undoEntry;
    } else {
      return this.graph.addEdges([newEdge]);
    }
  }

  deleteEdges(edgeIds: EdgeId[]) {
    this.graph.edit(() => {
      return this.graph.removeEdges(edgeIds);
    });
  }

  /// UNDO REDO
  undo() {
    this.graph.undo();
  }

  redo() {
    this.graph.redo();
  }

  hasUndo() {
    return this.graph.hasUndo();
  }

  hasRedo() {
    return this.graph.hasRedo();
  }

  /// SELECTION
  get selectedNodes(): NodeId[] {
    return this.graph.getSelectedNodes();
  }

  set selectedNodes(nodeIds: NodeId[]) {
    this.graph.setSelectedNodes(nodeIds);
  }

  get selectedEdges(): EdgeId[] {
    return this.graph.getSelectedEdges();
  }

  /** Edge ids explicitly chosen in React Flow (excludes incident-only highlights). */
  get explicitSelectedEdges(): EdgeId[] {
    return this.graph.getExplicitSelectedEdgeIds();
  }

  set selectedEdges(edgeIds: EdgeId[]) {
    this.graph.setSelectedEdges(edgeIds);
  }

  isNodeSelected(nodeId: NodeId): boolean {
    const node = this.graph.store.getNodeNullable(nodeId);

    if (!node) {
      return false;
    }

    return node.selected ?? false;
  }

  /// Validation
  missingRequiredInputs(nodeId: NodeId): string[] {
    return (
      this.graph.wasm.call((wasm) => {
        return wasm.inputIds(nodeId).filter((inputId) => {
          return wasm.isInputRequired(nodeId, inputId) && wasm.nodeInput(nodeId, inputId) === undefined;
        });
      }) ?? []
    );
  }

  invalidRequiredInputs(nodeId: NodeId): string[] {
    return (
      this.graph.wasm.call((wasm) => {
        return wasm.inputIds(nodeId).filter((inputId) => {
          return wasm.isInputRequired(nodeId, inputId) && !wasm.isInputValid(nodeId, inputId);
        });
      }) ?? []
    );
  }

  /**
   * Handles dropping an edge from a source output onto a target node's body.
   *
   * 1. First tries to connect to an existing empty input handle of a matching type.
   * 2. If all compatible handles are occupied, tries to add a new dynamic input
   *    handle (for nodes that support it, e.g. "Add another image input").
   *
   * @returns true if a connection was made, false otherwise.
   */
  tryAutoConnectOnDrop(
    sourceNodeId: NodeId,
    sourceHandleId: string,
    targetNodeId: NodeId,
    connectionHandleType: HandleType,
  ): boolean {
    if (sourceNodeId === targetNodeId) return false;
    if (this.graph.wouldConnectionResultInCycle(sourceNodeId, targetNodeId)) return false;

    const emptyInputKey = this.findFirstEmptyCompatibleInput(targetNodeId, connectionHandleType);
    if (emptyInputKey) {
      const targetHandle = getHandleId(targetNodeId, 'input', emptyInputKey);
      this.graph.edit(() => {
        const newEdge = this.graph.createEdge(sourceNodeId, targetNodeId, sourceHandleId, targetHandle);
        return this.graph.addEdges([newEdge]);
      });
      return true;
    }

    return (
      this.tryAddDynamicInput(sourceNodeId, sourceHandleId, targetNodeId, connectionHandleType) ||
      this.tryAddCompositorLayer(sourceNodeId, sourceHandleId, targetNodeId, connectionHandleType) ||
      this.tryAddPromptVariable(sourceNodeId, sourceHandleId, targetNodeId, connectionHandleType)
    );
  }

  /**
   * Finds the first unconnected input handle on the target node
   * that accepts the given handle type.
   */
  private findFirstEmptyCompatibleInput(targetNodeId: NodeId, connectionHandleType: HandleType): string | null {
    const inputIds = this.graph.getInputIds(targetNodeId);

    const backgroundFirst = inputIds.includes('background')
      ? ['background', ...inputIds.filter((id) => id !== 'background')]
      : inputIds;

    for (const inputId of backgroundFirst) {
      const isConnected = this.graph.getNodeInput(targetNodeId, inputId) !== undefined;
      if (isConnected) continue;
      const validTypes = this.graph.nodeValidInputTypes(targetNodeId, inputId);
      if (validTypes.includes(connectionHandleType)) {
        return inputId;
      }
    }

    return null;
  }

  /**
   * Tries to add a new dynamic input handle on a model node and connect to it.
   * Returns true if successful.
   */
  private tryAddDynamicInput(
    sourceNodeId: NodeId,
    sourceHandleId: string,
    targetNodeId: NodeId,
    connectionHandleType: HandleType,
  ): boolean {
    const targetData = this.graph.store.getNodeDataNullable<ModelBaseNodeData>(targetNodeId);
    if (!targetData) return false;

    const modelName = getModelIdentifier(targetData);
    if (!modelName) return false;

    const modelBaseView = new ModelBaseView(this.graph, targetNodeId);
    const config = modelBaseView.getDynamicInputConfig(modelName);
    if (!config || config.handleType !== connectionHandleType) return false;

    if (!Array.isArray(targetData.handles.input)) {
      const modelLimits = MODEL_INPUT_LIMITS[modelName];
      if (modelLimits) {
        const currentCount = Object.keys(targetData.handles.input).filter((key) => key.includes(config.prefix)).length;
        if (currentCount >= modelLimits.maxInputs) return false;
      }
    }

    this.graph.edit(() => {
      const { key, undoEntry } = modelBaseView.addInputHandleWithKey(config.prefix, config.handleType);
      const targetHandleId = getHandleId(targetNodeId, 'input', key);
      const newEdge = this.graph.createEdge(sourceNodeId, targetNodeId, sourceHandleId, targetHandleId);
      undoEntry.add(this.graph.addEdges([newEdge]));
      return undoEntry;
    });

    return true;
  }

  private tryAddCompositorLayer(
    sourceNodeId: NodeId,
    sourceHandleId: string,
    targetNodeId: NodeId,
    connectionHandleType: HandleType,
  ): boolean {
    const targetNode = this.graph.store.getNodeNullable(targetNodeId);
    if (!targetNode) return false;

    if (targetNode.type !== 'compv3') return false;

    const inputIds = this.graph.getInputIds(targetNodeId);
    if (inputIds.length === 0) return false;

    const validTypes = this.graph.nodeValidInputTypes(targetNodeId, inputIds[0]);
    if (!validTypes.includes(connectionHandleType)) return false;

    const compositorView = new CompositorView(this.graph, targetNodeId);

    this.graph.edit(() => {
      const { key, undoEntry } = compositorView.addNodeInputHandleWithKey();
      const targetHandleId = getHandleId(targetNodeId, 'input', key);
      const newEdge = this.graph.createEdge(sourceNodeId, targetNodeId, sourceHandleId, targetHandleId);
      undoEntry.add(this.graph.addEdges([newEdge]));
      return undoEntry;
    });

    return true;
  }

  private tryAddPromptVariable(
    sourceNodeId: NodeId,
    sourceHandleId: string,
    targetNodeId: NodeId,
    connectionHandleType: HandleType,
  ): boolean {
    if (connectionHandleType !== HandleType.Text) return false;

    const targetNode = this.graph.store.getNodeNullable(targetNodeId);
    if (!targetNode) return false;
    if (targetNode.type !== NodeType.PromptV3 && targetNode.type !== NodeType.Prompt) return false;

    const promptView = new PromptView(this.graph, targetNodeId);

    this.graph.edit(() => {
      const { key, undoEntry } = promptView.addInputHandleWithVariable();
      const targetHandleId = getHandleId(targetNodeId, 'input', key);
      const newEdge = this.graph.createEdge(sourceNodeId, targetNodeId, sourceHandleId, targetHandleId);
      undoEntry.add(this.graph.addEdges([newEdge]));
      return undoEntry;
    });

    return true;
  }
}

export type InvalidConnectionOptions = {
  errorId: 'self-connection' | 'cyclic';
  source: NodeId;
  sourceHandle: string | null;
  target: NodeId;
  targetHandle: string | null;
  additionalParams?: Record<string, unknown>;
};
